import React from 'react';
import BoardCardPreview from '../../pages/boards/BoardCardPreview';
import TableCardPreview from '../../pages/DatabasesComponents/TableCardPreview';
import SignupPreviewChatThread, {
  SignupPreviewAgentAvatar,
  SignupPreviewAgentRow,
} from './SignupPreviewChatThread';
import {
  buildPreviewChatsMessages,
  buildPreviewHomeChatMessages,
} from './signupPreviewUtils';
import './signupPreviewDemos.css';

const DEMO_BOARDS = [
  {
    name: 'Customer onboarding',
    stages: [
      { name: 'Intake', stageId: '1' },
      { name: 'Agent review', stageId: '2', kind: 'AGENT' },
      { name: 'Approved', stageId: '3' },
    ],
  },
  {
    name: 'Support tickets',
    stages: [
      { name: 'New', stageId: '4' },
      { name: 'In progress', stageId: '5' },
      { name: 'Resolved', stageId: '6' },
    ],
  },
];

const DEMO_TABLES = [
  {
    name: 'Contacts',
    columns: [
      { name: 'Name', type: 'text' },
      { name: 'Email', type: 'email' },
      { name: 'Company', type: 'text' },
    ],
  },
  {
    name: 'Deals',
    columns: [
      { name: 'Deal name', type: 'text' },
      { name: 'Amount', type: 'number' },
      { name: 'Stage', type: 'text' },
    ],
  },
];

const DEMO_AGENTS = ['Master Agent', 'Research assistant', 'Ops coordinator'];

const DEMO_TASKS = [
  { title: 'Review vendor contract', meta: 'Today · Board', status: 'Open' },
  { title: 'Approve onboarding flow', meta: 'Yesterday · Workflow', status: 'Waiting' },
  { title: 'Reply to customer thread', meta: 'Mon · Agent', status: 'In progress' },
];

const DEMO_AGENT_CARDS = [
  { name: 'Master Agent', description: 'Orchestrates agents and workspace tasks', tag: 'Orchestrator' },
  { name: 'Research assistant', description: 'Finds docs, data, and answers questions' },
  { name: 'Ops coordinator', description: 'Tracks boards, workflows, and follow-ups' },
];

const DEMO_TEAMS = [
  { name: 'Customer success', description: 'Onboarding and support handoffs', members: 3 },
  { name: 'Revenue ops', description: 'Deal review and pipeline updates', members: 2, draft: true },
];

const DEMO_WORKFLOWS = [
  { name: 'New customer onboarding', status: 'Published', icons: 4 },
  { name: 'Invoice approval', status: 'Draft', icons: 3 },
];

const DEMO_FILES = [
  { name: 'vendor-contract.pdf', meta: 'PDF · 842 KB' },
  { name: 'onboarding-checklist.docx', meta: 'DOCX · 128 KB' },
  { name: 'logo-dark.png', meta: 'PNG · 56 KB' },
];

const DEMO_ARTIFACTS = [
  { name: 'Customer summary', type: 'Report', status: 'Published', instances: 12 },
  { name: 'Deal scorecard', type: 'Dashboard', status: 'Draft', instances: 3 },
];

const DEMO_DOCS = [
  { title: 'Getting started', depth: 0 },
  { title: 'Agent playbooks', depth: 0, expanded: true },
  { title: 'Support handoffs', depth: 1, active: true },
  { title: 'Security policy', depth: 0 },
];

const DEMO_SECRETS = [
  { name: 'Slack', subtitle: 'Connected workspace', connected: true },
  { name: 'Google Drive', subtitle: 'Needs re-auth', connected: false },
  { name: 'Webhook trigger', subtitle: 'Custom secret', connected: true },
];

const DEMO_FUNCTIONS = [
  { name: 'Normalize address', slug: 'normalize-address', status: 'Published' },
  { name: 'Score lead', slug: 'score-lead', status: 'Draft' },
];

const DEMO_FORMS = [
  { name: 'Customer intake', kind: 'Form', status: 'Published' },
  { name: 'Vendor review', kind: 'Review task', status: 'Draft' },
];

const DEMO_MCP_SERVERS = [
  { name: 'Linear', status: 'Connected', tools: 8 },
  { name: 'Notion', status: 'Needs auth', tools: 0 },
];

const DEMO_HUMANS = [
  { name: 'Alex Chen', email: 'alex@company.com', role: 'Admin' },
  { name: 'Jamie Park', email: 'jamie@company.com', role: 'Member' },
];

const DEMO_TRASH = [
  { name: 'Legacy onboarding flow', status: 'In Trash' },
  { name: 'Old support workflow', status: 'Deleting soon' },
];

const DEMO_MORE_LINKS = [
  'Custom Functions',
  'Forms',
  'MCP Servers',
  'Humans',
  'Trash',
];

function DemoPageHeader({ title, subtitle }) {
  return (
    <div className="signup-demo__page-header">
      <h3 className="signup-demo__page-title">{title}</h3>
      {subtitle ? <p className="signup-demo__page-subtitle">{subtitle}</p> : null}
    </div>
  );
}

function SignupPreviewHome({ displayName, workEmail }) {
  const greeting = displayName ? `Hello, ${displayName}` : 'Hello';
  const homeChatMessages = buildPreviewHomeChatMessages(workEmail);

  return (
    <div className="signup-demo signup-demo--home">
      <DemoPageHeader title={greeting} subtitle="Your workspace overview" />
      <div className="signup-demo__home-grid">
        <div className="signup-demo__panel signup-demo__panel--chat">
          <div className="signup-demo__chat-layout">
            <div className="signup-demo__session-list">
              <div className="signup-demo__session-list-header">Sessions</div>
              <div className="signup-demo__session-row signup-demo__session-row--active">
                <span className="signup-demo__session-dot" />
                New conversation
              </div>
              <div className="signup-demo__session-row">Weekly planning</div>
              <div className="signup-demo__session-row">Customer follow-up</div>
            </div>
            <div className="signup-demo__chat-main">
              <SignupPreviewChatThread messages={homeChatMessages} />
            </div>
          </div>
        </div>
        <div className="signup-demo__overview">
          <div className="signup-demo__overview-card">
            <div className="signup-demo__overview-label">Agents</div>
            {DEMO_AGENTS.slice(0, 2).map(name => (
              <div key={name} className="signup-demo__overview-row signup-demo__overview-row--agent">
                <SignupPreviewAgentAvatar name={name} size={16} />
                <span>{name}</span>
              </div>
            ))}
          </div>
          <div className="signup-demo__overview-card">
            <div className="signup-demo__overview-label">Recent conversations</div>
            <div className="signup-demo__overview-row">Customer onboarding</div>
            <div className="signup-demo__overview-row">Ops weekly sync</div>
          </div>
          <div className="signup-demo__overview-card">
            <div className="signup-demo__overview-label">Credits used</div>
            <div className="signup-demo__progress">
              <span style={{ width: '28%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignupPreviewChats({ workEmail }) {
  const chatsMessages = buildPreviewChatsMessages(workEmail);

  return (
    <div className="signup-demo signup-demo--chats">
      <DemoPageHeader title="Chats" />
      <div className="signup-demo__chats-layout">
        <div className="signup-demo__agent-list">
          <div className="signup-demo__section-label">Direct messages</div>
          {DEMO_AGENTS.map((name, index) => (
            <SignupPreviewAgentRow key={name} name={name} active={index === 0} />
          ))}
        </div>
        <div className="signup-demo__panel signup-demo__panel--chat-main">
          <div className="signup-demo__chat-main signup-demo__chat-main--full">
            <SignupPreviewChatThread messages={chatsMessages} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SignupPreviewBoards() {
  return (
    <div className="signup-demo signup-demo--boards">
      <DemoPageHeader title="Boards" subtitle="Agentic kanban boards" />
      <div className="signup-demo__card-grid">
        {DEMO_BOARDS.map(board => (
          <div key={board.name} className="signup-demo__entity-card">
            <div className="signup-demo__entity-card-header">{board.name}</div>
            <div className="signup-demo__entity-card-body">
              <BoardCardPreview stages={board.stages} layout="flow" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewInbox() {
  return (
    <div className="signup-demo signup-demo--inbox">
      <DemoPageHeader title="Tasks" subtitle="Your inbox across agents, boards, and workflows" />
      <div className="signup-demo__inbox-layout">
        <div className="signup-demo__source-panel">
          <div className="signup-demo__source-row signup-demo__source-row--active">All tasks</div>
          <div className="signup-demo__source-row">Agents</div>
          <div className="signup-demo__source-row">Boards</div>
          <div className="signup-demo__source-row">Workflows</div>
        </div>
        <div className="signup-demo__task-list">
          <div className="signup-demo__date-label">Today</div>
          {DEMO_TASKS.map(task => (
            <div key={task.title} className="signup-demo__task-row">
              <div className="signup-demo__task-copy">
                <div className="signup-demo__task-title">{task.title}</div>
                <div className="signup-demo__task-meta">{task.meta}</div>
              </div>
              <span className="signup-demo__status-pill">{task.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignupPreviewTables() {
  return (
    <div className="signup-demo signup-demo--tables">
      <DemoPageHeader title="Tables" subtitle="Built-in databases for your workspace" />
      <div className="signup-demo__search-row">
        <div className="signup-demo__search-field">Search tables</div>
      </div>
      <div className="signup-demo__card-grid">
        {DEMO_TABLES.map(table => (
          <div key={table.name} className="signup-demo__entity-card">
            <div className="signup-demo__entity-card-header">{table.name}</div>
            <div className="signup-demo__entity-card-body">
              <TableCardPreview columns={table.columns} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewAgents() {
  return (
    <div className="signup-demo signup-demo--agents">
      <DemoPageHeader title="Agents" subtitle="AI agents for your workspace" />
      <div className="signup-demo__search-row">
        <div className="signup-demo__search-field">Search agents</div>
      </div>
      <div className="signup-demo__card-grid signup-demo__card-grid--tall">
        {DEMO_AGENT_CARDS.map(agent => (
          <div key={agent.name} className="signup-demo__resource-card">
            <div className="signup-demo__resource-card-top">
              <SignupPreviewAgentAvatar name={agent.name} size={28} />
              <div className="signup-demo__resource-card-copy">
                <div className="signup-demo__resource-card-title">{agent.name}</div>
                <div className="signup-demo__resource-card-desc">{agent.description}</div>
              </div>
            </div>
            {agent.tag ? (
              <span className="signup-demo__tag-pill">{agent.tag}</span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewTeams() {
  return (
    <div className="signup-demo signup-demo--teams">
      <DemoPageHeader title="Teams" subtitle="Groups of agents working together" />
      <div className="signup-demo__card-grid">
        {DEMO_TEAMS.map(team => (
          <div key={team.name} className="signup-demo__resource-card signup-demo__resource-card--team">
            <div className="signup-demo__team-avatars">
              {Array.from({ length: team.members }).map((_, index) => (
                <span key={index} className="signup-demo__team-avatar-dot" />
              ))}
            </div>
            <div className="signup-demo__resource-card-title">{team.name}</div>
            <div className="signup-demo__resource-card-desc">{team.description}</div>
            {team.draft ? <span className="signup-demo__status-pill">Draft</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewWorkflows() {
  return (
    <div className="signup-demo signup-demo--workflows">
      <DemoPageHeader title="Workflows" subtitle="Automations across your workspace" />
      <div className="signup-demo__card-grid">
        {DEMO_WORKFLOWS.map(workflow => (
          <div key={workflow.name} className="signup-demo__resource-card signup-demo__resource-card--workflow">
            <div className="signup-demo__resource-card-title">{workflow.name}</div>
            <div className="signup-demo__workflow-icons">
              {Array.from({ length: workflow.icons }).map((_, index) => (
                <span key={index} className="signup-demo__workflow-icon" />
              ))}
            </div>
            <span
              className={[
                'signup-demo__status-pill',
                workflow.status === 'Published'
                  ? 'signup-demo__status-pill--published'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {workflow.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewFiles() {
  return (
    <div className="signup-demo signup-demo--files">
      <DemoPageHeader title="Files" subtitle="Documents across workflows and agents" />
      <div className="signup-demo__master-detail">
        <div className="signup-demo__source-panel">
          <div className="signup-demo__source-row signup-demo__source-row--active">Workflows</div>
          <div className="signup-demo__source-row">Agents</div>
        </div>
        <div className="signup-demo__list-panel">
          {DEMO_FILES.map(file => (
            <div key={file.name} className="signup-demo__list-row">
              <span className="signup-demo__file-icon" />
              <div className="signup-demo__list-copy">
                <div className="signup-demo__list-title">{file.name}</div>
                <div className="signup-demo__list-meta">{file.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignupPreviewArtifacts() {
  return (
    <div className="signup-demo signup-demo--artifacts">
      <DemoPageHeader title="Artifacts" subtitle="Structured outputs from agents" />
      <div className="signup-demo__card-grid">
        {DEMO_ARTIFACTS.map(artifact => (
          <div key={artifact.name} className="signup-demo__resource-card signup-demo__resource-card--artifact">
            <div className="signup-demo__artifact-thumb" />
            <div className="signup-demo__resource-card-title">{artifact.name}</div>
            <div className="signup-demo__list-meta">
              {artifact.type} · {artifact.instances} instances
            </div>
            <span className="signup-demo__status-pill">{artifact.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewDocs() {
  return (
    <div className="signup-demo signup-demo--docs">
      <DemoPageHeader title="Docs" subtitle="Knowledge base for agents and teams" />
      <div className="signup-demo__master-detail signup-demo__master-detail--wide-sidebar">
        <div className="signup-demo__sidebar-panel signup-demo__tree-panel">
          {DEMO_DOCS.map(doc => (
            <div
              key={doc.title}
              className={[
                'signup-demo__tree-row',
                doc.depth ? `signup-demo__tree-row--depth-${doc.depth}` : '',
                doc.active ? 'signup-demo__tree-row--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="signup-demo__tree-chevron">{doc.expanded ? '▾' : '▸'}</span>
              <span className="signup-demo__tree-icon" />
              <span className="signup-demo__tree-label">{doc.title}</span>
            </div>
          ))}
        </div>
        <div className="signup-demo__docs-editor signup-demo__detail-pane">
          <div className="signup-demo__docs-editor-title">Support handoffs</div>
          <div className="signup-demo__generic-row" />
          <div className="signup-demo__generic-row" />
          <div className="signup-demo__generic-row signup-demo__generic-row--short" />
        </div>
      </div>
    </div>
  );
}

function SignupPreviewSecrets() {
  return (
    <div className="signup-demo signup-demo--secrets">
      <DemoPageHeader title="Secrets" subtitle="Integrations and credentials" />
      <div className="signup-demo__master-detail signup-demo__master-detail--wide-sidebar">
        <div className="signup-demo__sidebar-panel signup-demo__list-panel signup-demo__list-panel--sidebar">
          <div className="signup-demo__section-label">App integrations</div>
          {DEMO_SECRETS.map((secret, index) => (
            <div
              key={secret.name}
              className={[
                'signup-demo__sidebar-row',
                index === 0 ? 'signup-demo__sidebar-row--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span
                className={[
                  'signup-demo__connection-dot',
                  secret.connected ? 'signup-demo__connection-dot--on' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              <div className="signup-demo__list-copy">
                <div className="signup-demo__list-title">{secret.name}</div>
                <div className="signup-demo__list-meta">{secret.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="signup-demo__detail-pane signup-demo__empty-pane">
          <span className="signup-demo__empty-icon" />
          Select a secret to view details
        </div>
      </div>
    </div>
  );
}

function SignupPreviewCustomFunctions() {
  return (
    <div className="signup-demo signup-demo--functions">
      <DemoPageHeader title="Custom Functions" subtitle="Reusable code for workflows" />
      <div className="signup-demo__master-detail signup-demo__master-detail--wide-sidebar">
        <div className="signup-demo__sidebar-panel signup-demo__list-panel signup-demo__list-panel--sidebar">
          {DEMO_FUNCTIONS.map((fn, index) => (
            <div
              key={fn.name}
              className={[
                'signup-demo__sidebar-row',
                index === 0 ? 'signup-demo__sidebar-row--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="signup-demo__fn-icon" />
              <div className="signup-demo__list-copy">
                <div className="signup-demo__list-title">{fn.name}</div>
                <div className="signup-demo__sidebar-meta-row">
                  <div className="signup-demo__list-meta">{fn.slug}</div>
                  <span className="signup-demo__status-pill signup-demo__status-pill--compact">
                    {fn.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="signup-demo__detail-pane signup-demo__empty-pane">
          Select a function to edit
        </div>
      </div>
    </div>
  );
}

function SignupPreviewForms() {
  return (
    <div className="signup-demo signup-demo--forms">
      <DemoPageHeader title="Forms" subtitle="Collect input and review tasks" />
      <div className="signup-demo__master-detail signup-demo__master-detail--wide-sidebar">
        <div className="signup-demo__sidebar-panel signup-demo__list-panel signup-demo__list-panel--sidebar">
          {DEMO_FORMS.map((form, index) => (
            <div
              key={form.name}
              className={[
                'signup-demo__sidebar-row',
                index === 0 ? 'signup-demo__sidebar-row--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="signup-demo__form-icon" />
              <div className="signup-demo__list-copy">
                <div className="signup-demo__list-title">{form.name}</div>
                <div className="signup-demo__sidebar-meta-row">
                  <div className="signup-demo__list-meta">{form.kind}</div>
                  <span className="signup-demo__status-pill signup-demo__status-pill--compact">
                    {form.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="signup-demo__detail-pane signup-demo__empty-pane">
          Select a form to preview
        </div>
      </div>
    </div>
  );
}

function SignupPreviewMcpServers() {
  return (
    <div className="signup-demo signup-demo--mcp">
      <DemoPageHeader title="MCP Servers" subtitle="Connect external tools to agents" />
      <div className="signup-demo__card-grid">
        {DEMO_MCP_SERVERS.map(server => (
          <div key={server.name} className="signup-demo__resource-card signup-demo__resource-card--mcp">
            <div className="signup-demo__mcp-preview" />
            <div className="signup-demo__resource-card-title">{server.name}</div>
            <div className="signup-demo__list-meta">{server.tools} tools</div>
            <span
              className={[
                'signup-demo__status-pill',
                server.status === 'Connected'
                  ? 'signup-demo__status-pill--published'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {server.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewHumans() {
  return (
    <div className="signup-demo signup-demo--humans">
      <DemoPageHeader title="Humans" subtitle="People in your organization" />
      <div className="signup-demo__tabs">
        <span className="signup-demo__tab signup-demo__tab--active">Active</span>
        <span className="signup-demo__tab">Invited</span>
        <span className="signup-demo__tab">Disabled</span>
      </div>
      <div className="signup-demo__table">
        {DEMO_HUMANS.map(person => (
          <div key={person.email} className="signup-demo__table-row">
            <div className="signup-demo__table-user">
              <span className="signup-demo__human-avatar">{person.name.charAt(0)}</span>
              <div className="signup-demo__list-copy">
                <div className="signup-demo__list-title">{person.name}</div>
                <div className="signup-demo__list-meta">{person.email}</div>
              </div>
            </div>
            <span className="signup-demo__role-pill">{person.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewTrash() {
  return (
    <div className="signup-demo signup-demo--trash">
      <DemoPageHeader title="Trash" subtitle="Deleted workflows" />
      <div className="signup-demo__card-grid">
        {DEMO_TRASH.map(item => (
          <div key={item.name} className="signup-demo__resource-card signup-demo__resource-card--workflow">
            <div className="signup-demo__resource-card-title">{item.name}</div>
            <div className="signup-demo__workflow-icons">
              <span className="signup-demo__workflow-icon" />
              <span className="signup-demo__workflow-icon" />
            </div>
            <span className="signup-demo__status-pill">{item.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupPreviewMore() {
  return (
    <div className="signup-demo signup-demo--more">
      <DemoPageHeader title="More" subtitle="Additional workspace resources" />
      <div className="signup-demo__more-grid">
        {DEMO_MORE_LINKS.map(label => (
          <div key={label} className="signup-demo__more-card">
            <div className="signup-demo__more-card-title">{label}</div>
            <div className="signup-demo__list-meta">Open from sidebar</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SignupPreviewPageDemo({ page, displayName, workEmail }) {
  switch (page) {
    case 'Home':
      return <SignupPreviewHome displayName={displayName} workEmail={workEmail} />;
    case 'Chats':
      return <SignupPreviewChats workEmail={workEmail} />;
    case 'Boards':
      return <SignupPreviewBoards />;
    case 'Inbox':
      return <SignupPreviewInbox />;
    case 'Tables':
      return <SignupPreviewTables />;
    case 'Agents':
      return <SignupPreviewAgents />;
    case 'Teams':
      return <SignupPreviewTeams />;
    case 'Workflows':
      return <SignupPreviewWorkflows />;
    case 'Files':
      return <SignupPreviewFiles />;
    case 'Artifacts':
      return <SignupPreviewArtifacts />;
    case 'Docs':
      return <SignupPreviewDocs />;
    case 'Secrets':
      return <SignupPreviewSecrets />;
    case 'Custom Functions':
      return <SignupPreviewCustomFunctions />;
    case 'Forms':
      return <SignupPreviewForms />;
    case 'MCP Servers':
      return <SignupPreviewMcpServers />;
    case 'Humans':
      return <SignupPreviewHumans />;
    case 'Trash':
      return <SignupPreviewTrash />;
    case 'More':
      return <SignupPreviewMore />;
    default:
      return <SignupPreviewHome displayName={displayName} />;
  }
}
